import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { addBreadcrumb, logException } from "@wss/error-tracking";
import { Throwable } from "@wss/error-tracking/throwable";
import { env } from "~/common/Environment";
type Recaptcha = {
  execute: (key: string, opts?: {
    action?: string;
  }) => Promise<string>;
  ready: (cb: () => void) => void;
};
const recaptchaSiteKeys = {
  local: "6Lf0ILAUAAAAAAG_5Z5G2ihgXtxsjvw6CKYt3OHs",
  development: "6Lf0ILAUAAAAAAG_5Z5G2ihgXtxsjvw6CKYt3OHs",
  test: "6Lf0ILAUAAAAAAG_5Z5G2ihgXtxsjvw6CKYt3OHs",
  production: "6LcMB7EUAAAAAIBgYhbNBrCNyKHlQtMgVYTjpT8V"
};

/** Preflight uses the dev key */
const recaptchaSiteKey = recaptchaSiteKeys[env.isPreflight ? "development" : env.current];
const scriptId = "google-recaptcha-v3";
const recaptchaOnloadSubs = new Set<() => void>();
const recaptchaLoadTimeoutMs = 5000;
let instance: Recaptcha | null = null;
function onRecaptchaLoaded() {
  // For loops are sync, forEach is not
  for (const sub of recaptchaOnloadSubs) {
    sub();
  }
  recaptchaOnloadSubs.clear();
}
export type RecaptchaContextType = {
  /** See {@link useReCaptcha } for more information on how to use ReCaptcha. */
  execute: (action?: string) => Promise<Throwable<string, unknown>>;
  /** Allows a consumer to wait for the re-captcha script to be loaded */
  onLoad: (cb: () => void) => void;
};
export const RecaptchaContext = createContext<RecaptchaContextType | null>(null);

// Clean up global state for tests
export const cleanupRecaptcha = () => {
  if (instance) {
    instance = null;
  }
  recaptchaOnloadSubs.clear();
};
type ReCaptchaProviderProps = {
  action?: string;
  language?: string;
  useEnterprise?: boolean;
  children?: ReactNode;
};

/**
 * Provides all pages in SF with the ability to use ReCaptcha.
 *
 * See {@link useReCaptcha } for more information on how to use ReCaptcha.
 *
 * {@link https://developers.google.com/recaptcha/docs/v3 ReCaptcha Documentation}
 *
 * {@link https://github.com/t49tran/react-google-recaptcha-v3 Github Reference}
 */
export function ReCaptchaProvider({
  action: defaultAction,
  useEnterprise = true,
  language,
  children
}: Readonly<ReCaptchaProviderProps>): JSX.Element {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const subs = useRef<Set<() => void>>(new Set());
  const onLoad: RecaptchaContextType["onLoad"] = useCallback(function handleOnLoad(cb: () => void): void {
    if (!instance) {
      subs.current.add(cb);
      return;
    }
    cb();
  }, []);
  const execute: RecaptchaContextType["execute"] = useCallback(async function onExecute(action?: string): ReturnType<RecaptchaContextType["execute"]> {
    if (!instance) {
      const error = new Error("Google Recaptcha has not been loaded");
      await logException(error);
      return {
        isError: true,
        error
      };
    }
    try {
      const result = await instance.execute(recaptchaSiteKey, {
        action: action ?? defaultAction
      });
      return {
        isError: false,
        value: result
      };
    } catch (error) {
      await logException(new Error("Recaptcha failed to execute. See error cause.", {
        cause: error
      }));
      return {
        isError: true,
        error
      };
    }
  }, [defaultAction]);
  const onScriptLoaded = useCallback(function handleRecaptchaScriptLoaded() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (instance) return;
    const ref: Recaptcha = useEnterprise ? window.grecaptcha.enterprise : window.grecaptcha;
    ref.ready(function handleRecaptchaReady() {
      instance = ref;
      subs.current.forEach(sub => sub());
      subs.current.clear();
    });
  }, [useEnterprise]);
  const onRecaptchaLoadTimeout = useCallback(async function handleRecaptchaLoadTimeout() {
    await addBreadcrumb({
      message: "ReCaptcha script failed to load within timeout period. This is most likely due to a slow or bad connection.",
      data: {
        timeout: recaptchaLoadTimeoutMs
      }
    }, "error");
  }, []);
  const onLoadWithTimeout = useCallback(function handleOnLoadWithTimeout() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(onRecaptchaLoadTimeout, recaptchaLoadTimeoutMs);
    recaptchaOnloadSubs.add(onScriptLoaded);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [onRecaptchaLoadTimeout, onScriptLoaded]);
  useEffect(function initializeScript() {
    // Already loaded and ready for use
    if (instance) return;
    // Script has been added but we may have timed out, re-assign instance.
    if (window.grecaptcha) return onScriptLoaded();
    // We have the script, some other instance of this provider added it, this component just needs to make sure to assign the ref once the script loads
    if (document.getElementById(scriptId)) return onLoadWithTimeout();
    if (!window.recaptchaOnLoad) {
      window.recaptchaOnLoad = onRecaptchaLoaded;
    }
    const scriptName = useEnterprise ? "enterprise.js" : "api.js";
    const url = new URL(`https://www.google.com/recaptcha/${scriptName}`);
    url.searchParams.append("onload", "recaptchaOnLoad");
    url.searchParams.append("render", recaptchaSiteKey);
    if (language) {
      url.searchParams.append("hl", language);
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = url.href;
    document.head.appendChild(script);
    return onLoadWithTimeout();
  }, [language, onLoadWithTimeout, onScriptLoaded, useEnterprise]);
  const value = useMemo(() => ({
    execute,
    onLoad
  }), [execute, onLoad]);
  return <RecaptchaContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="ReCaptchaProvider" data-sentry-source-file="re-captcha-provider.tsx">
      {children}
    </RecaptchaContext.Provider>;
}